import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  Pressable,
  Image,
  TextInput,
  ActivityIndicator,
  RefreshControl,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { Text, Theme, InsightListItem } from '../../components/ui';
import { auth } from '../../config/firebase';
import { PrivateProfileService } from '../../services/privateProfileService';
import { BookService } from '../../services/bookService';
import { FireBook, FireInsight } from '../../models/FirestoreModels';
import { placeholders } from '../../assets/placeholders';
import { useNavigationLogging } from '../../hooks/useNavigationLogging';
import MyReadingListSection from '../Dashboard/components/MyReadingListSection';

interface SavedInsightWithBook {
  insight: FireInsight;
  book: FireBook;
}

interface BookWithInsights {
  book: FireBook;
  insights: FireInsight[];
}

const SavedScreen: React.FC = () => {
  useNavigationLogging('SavedScreen');
  
  const navigation = useNavigation();
  const [searchQuery, setSearchQuery] = useState('');
  const [savedByBook, setSavedByBook] = useState<BookWithInsights[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Reload saved insights whenever the screen comes into focus
  useFocusEffect(
    useCallback(() => {
      loadSavedInsights();
    }, [])
  );

  const loadSavedInsights = async (isRefreshing: boolean = false) => {
    try {
      if (isRefreshing) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      const userId = auth.currentUser?.uid;
      if (!userId) {
        setLoading(false);
        return;
      }

      // Get user's saved/completed insights from profile
      const profileResult = await PrivateProfileService.getProfile(userId);
      
      if (!profileResult.success || !profileResult.data) {
        setLoading(false);
        return;
      }

      // Collect all saved/completed insights across all books
      const booksProgress = profileResult.data.booksProgress || {};
      const savedInsightsByBook: Map<string, FireInsight[]> = new Map();
      const bookDetailsCache: Map<string, FireBook> = new Map();

      // Collect all saved insights from insightsProgress
      const savedInsightsMap: Map<string, Array<{insight: FireInsight; savedAt: number}>> = new Map();
      
      for (const [insightId, progress] of Object.entries(profileResult.data.insightsProgress || {})) {
        // Only include insights that are explicitly saved
        if (progress.isSaved) {
          const bookId = progress.bookId;
          const savedAt = progress.savedAt || Date.now() / 1000;
          
          if (!savedInsightsMap.has(bookId)) {
            savedInsightsMap.set(bookId, []);
          }
          
          // We'll load the insight details later
          savedInsightsMap.get(bookId)!.push({ 
            insight: { id: insightId } as FireInsight, 
            savedAt 
          });
        }
      }
      
      // Load all books in parallel (much faster!)
      const bookLoadPromises = Array.from(savedInsightsMap.entries()).map(
        async ([bookId, savedInsights]) => {
          const cleanBookId = bookId.includes('_') ? bookId.split('_')[0] : bookId;
          const bookResult = await BookService.getBookDetail(cleanBookId, 'en');
          
          if (bookResult.success && bookResult.data?.book) {
            const book = bookResult.data.book;
            const allInsights = bookResult.data.insights || [];
            
            // Match saved insights with full insight data
            const fullSavedInsights = savedInsights
              .map(({ insight: partialInsight, savedAt }) => {
                const fullInsight = allInsights.find(i => i.id === partialInsight.id);
                return fullInsight ? { insight: fullInsight, savedAt } : null;
              })
              .filter((item): item is {insight: FireInsight; savedAt: number} => item !== null)
              // Sort by savedAt descending (newest first)
              .sort((a, b) => b.savedAt - a.savedAt);
            
            if (fullSavedInsights.length > 0) {
              return { bookId, book, insights: fullSavedInsights.map(item => item.insight) };
            }
          }
          return null;
        }
      );
      
      const loadedBooks = (await Promise.all(bookLoadPromises)).filter(
        (item): item is { bookId: string; book: FireBook; insights: FireInsight[] } => item !== null
      );
      
      // Build the maps
      for (const { bookId, book, insights } of loadedBooks) {
        bookDetailsCache.set(bookId, book);
        savedInsightsByBook.set(bookId, insights);
      }

      // Convert to array format with timestamp for sorting
      const result: Array<BookWithInsights & {newestSavedAt: number}> = [];
      for (const [bookId, insights] of savedInsightsByBook.entries()) {
        const book = bookDetailsCache.get(bookId);
        if (book) {
          // Get the newest savedAt timestamp for this book
          const newestSavedAt = Math.max(
            ...Array.from(Object.values(profileResult.data.insightsProgress || {}))
              .filter(p => p.isSaved && p.bookId === bookId && p.savedAt)
              .map(p => p.savedAt!)
          );
          
          result.push({ book, insights, newestSavedAt });
        }
      }
      
      // Sort books by newest saved insight (most recent first)
      result.sort((a, b) => b.newestSavedAt - a.newestSavedAt);

      setSavedByBook(result);
    } catch (error) {
      console.error('Error loading saved insights:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    loadSavedInsights(true);
  };

  const handleInsightPress = (book: FireBook, insight: FireInsight, insights: FireInsight[]) => {
    const currentIndex = insights.findIndex(i => i.id === insight.id);
    // @ts-ignore - Navigation types
    navigation.navigate('InsightDetail', {
      insights,
      book,
      bookTitle: book.title,
      bookCoverUrl: book.coverImageUrl || book.coverImageFullUrl,
      currentIndex,
      isMixedInsights: true, // Don't include book introduction - just saved insights
      fromRoute: 'Saved',
    });
  };

  const handlePlayAll = (book: FireBook, insights: FireInsight[]) => {
    if (insights.length === 0) return;
    
    // @ts-ignore - Navigation types
    navigation.navigate('InsightDetail', {
      insights,
      book,
      bookTitle: book.title,
      bookCoverUrl: book.coverImageUrl || book.coverImageFullUrl,
      currentIndex: 0,
      isMixedInsights: true, // Don't include book introduction
      fromRoute: 'Saved',
    });
  };

  const totalInsights = savedByBook.reduce((sum, item) => sum + item.insights.length, 0);
  
  const filteredBooks = searchQuery.trim().length > 0
    ? savedByBook.filter(item => 
        item.book.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.insights.some(insight => 
          insight.title.toLowerCase().includes(searchQuery.toLowerCase())
        )
      )
    : savedByBook;

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={Theme.colors.primary} />
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerTop}>
          <View style={styles.headerInfo}>
            <Text style={styles.headerTitle}>My Library</Text>
            <Text style={styles.headerSubtitle}>
              {totalInsights} insight{totalInsights !== 1 ? 's' : ''} from {savedByBook.length} book{savedByBook.length !== 1 ? 's' : ''}
            </Text>
          </View>
          <View style={styles.iconBadge}>
            <Ionicons name="heart" size={24} color={Theme.colors.primary} />
          </View>
        </View>

        {/* Search Bar */}
        <View style={styles.searchBar}>
          <Ionicons 
            name="search-outline" 
            size={20} 
            color={Theme.colors.textSecondary} 
            style={styles.searchIcon}
          />
          <TextInput
            style={styles.searchInput}
            placeholder="Search your saved insights..."
            placeholderTextColor={Theme.colors.textSecondary}
            value={searchQuery}
            onChangeText={setSearchQuery}
          />
          {searchQuery.length > 0 && (
            <Pressable onPress={() => setSearchQuery('')}>
              <Ionicons 
                name="close-circle" 
                size={20} 
                color={Theme.colors.textSecondary} 
              />
            </Pressable>
          )}
        </View>
      </View>

      <ScrollView 
        style={styles.scrollView} 
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            tintColor={Theme.colors.primary}
            colors={[Theme.colors.primary]}
          />
        }
      >
        {/* My Reading List Section */}
        <View style={styles.readingListContainer}>
          <MyReadingListSection />
        </View>

        {filteredBooks.length > 0 ? (
          <View style={styles.content}>
            {filteredBooks.map((item) => (
              <View key={item.book.id} style={styles.bookSection}>
                {/* Book Header */}
                <View style={styles.bookHeader}>
                  <Pressable 
                    style={styles.bookCover}
                    onPress={() => {
                      const cleanBookId = item.book.id.includes('_') ? item.book.id.split('_')[0] : item.book.id;
                      // @ts-ignore - Navigation types
                      navigation.navigate('BookDetail', { 
                        bookId: cleanBookId, 
                        languageCode: item.book.languageCode || 'en' 
                      });
                    }}
                  >
                    <Image
                      source={
                        item.book.coverImageUrl || item.book.coverImageFullUrl
                          ? { uri: item.book.coverImageUrl || item.book.coverImageFullUrl }
                          : placeholders.bookCover
                      }
                      style={styles.bookCoverImage}
                      resizeMode="cover"
                    />
                  </Pressable>

                  <View style={styles.bookInfo}>
                    <Pressable
                      onPress={() => {
                        const cleanBookId = item.book.id.includes('_') ? item.book.id.split('_')[0] : item.book.id;
                        // @ts-ignore - Navigation types
                        navigation.navigate('BookDetail', { 
                          bookId: cleanBookId, 
                          languageCode: item.book.languageCode || 'en' 
                        });
                      }}
                    >
                      <Text style={styles.bookTitle} numberOfLines={2}>
                        {item.book.title}
                      </Text>
                    </Pressable>
                    <Text style={styles.bookAuthor} numberOfLines={1}>
                      {item.book.author}
                    </Text>
                    
                    <Pressable
                      style={styles.playAllButton}
                      onPress={() => handlePlayAll(item.book, item.insights)}
                    >
                      <Ionicons name="play" size={14} color={Theme.colors.primary} />
                      <Text style={styles.playAllText}>
                        Play All ({item.insights.length})
                      </Text>
                    </Pressable>
                  </View>
                </View>

                {/* Insights List */}
                <View style={styles.insightsContainer}>
                  {item.insights.map((insight, index) => (
                    <InsightListItem
                      key={insight.id}
                      title={insight.title}
                      isRead={true}
                      isLast={index === item.insights.length - 1}
                      showNumberBadge={false}
                      onPress={() => handleInsightPress(item.book, insight, item.insights)}
                    />
                  ))}
                </View>
              </View>
            ))}
          </View>
        ) : (
          /* Empty State */
          <View style={styles.emptyState}>
            <View style={styles.emptyIconContainer}>
              <Ionicons name="heart-outline" size={40} color="rgba(20, 184, 166, 0.3)" />
            </View>
            <Text style={styles.emptyTitle}>No Saved Insights Yet</Text>
            <Text style={styles.emptyDescription}>
              When you save insights you love, they'll appear here for easy access anytime.
            </Text>
          </View>
        )}

        {/* Support Widget */}
        <View style={styles.supportWidget}>
          <View style={styles.supportContent}>
            <Ionicons name="information-circle-outline" size={20} color={Theme.colors.textSecondary} />
            <View style={styles.supportTextContainer}>
              <Text style={styles.supportText}>
                Updated from an older version? If you don't see your saved items, contact our support and we'll import them for you.
              </Text>
            </View>
          </View>
          <Pressable
            style={styles.supportButton}
            onPress={() => {
              // @ts-ignore - Navigation types
              navigation.navigate('Profile', {
                screen: 'ContactUs',
                params: {
                  category: 'support',
                  subject: 'Missing Saved Items After App Update',
                  message: 'Hi, I updated the app from an older version and I don\'t see my saved items. Could you please help import them?',
                },
              });
            }}
          >
            <Text style={styles.supportButtonText}>Contact Support</Text>
          </Pressable>
        </View>

        <View style={styles.bottomPadding} />
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'rgba(240, 253, 250, 0.3)', // Very light teal tint
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  header: {
    paddingHorizontal: Theme.spacing.xl,
    paddingTop: Theme.spacing.lg,
    paddingBottom: Theme.spacing.lg,
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.08)',
  },
  headerTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: Theme.spacing.xl,
  },
  headerInfo: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 24,
    lineHeight: 32,
    fontFamily: Theme.typography.fontFamily.semiBold,
    color: Theme.colors.textPrimary,
    marginBottom: 4,
  },
  headerSubtitle: {
    fontSize: 14,
    fontFamily: Theme.typography.fontFamily.regular,
    color: Theme.colors.textSecondary,
  },
  iconBadge: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: 'rgba(20, 184, 166, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  searchBar: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.05)',
    borderRadius: Theme.borderRadius.lg,
    paddingHorizontal: Theme.spacing.lg,
    height: 48,
    gap: Theme.spacing.sm,
  },
  searchIcon: {
    flexShrink: 0,
  },
  searchInput: {
    flex: 1,
    fontSize: 15,
    fontFamily: Theme.typography.fontFamily.regular,
    color: Theme.colors.textPrimary,
    padding: 0,
  },
  scrollView: {
    flex: 1,
  },
  readingListContainer: {
    paddingTop: Theme.spacing.xl,
  },
  content: {
    paddingHorizontal: Theme.spacing.xl,
    paddingTop: Theme.spacing.xl,
    gap: Theme.spacing.xxxl,
  },
  bookSection: {
    marginBottom: Theme.spacing.xl,
  },
  bookHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: Theme.spacing.md,
    marginBottom: Theme.spacing.lg,
  },
  bookCover: {
    width: 48,
    height: 72,
    borderRadius: Theme.borderRadius.md,
    overflow: 'hidden',
    backgroundColor: '#f3f4f6',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
    flexShrink: 0,
  },
  bookCoverImage: {
    width: '100%',
    height: '100%',
  },
  bookInfo: {
    flex: 1,
    minWidth: 0,
    gap: 4,
  },
  bookTitle: {
    fontSize: 14,
    fontFamily: Theme.typography.fontFamily.medium,
    color: Theme.colors.textPrimary,
    lineHeight: 20,
  },
  bookAuthor: {
    fontSize: 12,
    fontFamily: Theme.typography.fontFamily.regular,
    color: Theme.colors.textSecondary,
    marginBottom: 4,
  },
  playAllButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: Theme.spacing.md,
    paddingVertical: Theme.spacing.xs,
    backgroundColor: 'rgba(20, 184, 166, 0.1)',
    borderRadius: Theme.borderRadius.sm,
    borderWidth: 1,
    borderColor: 'rgba(20, 184, 166, 0.2)',
    alignSelf: 'flex-start',
  },
  playAllText: {
    fontSize: 12,
    fontFamily: Theme.typography.fontFamily.medium,
    color: Theme.colors.primary,
  },
  insightsContainer: {
    backgroundColor: '#f9fafb',
    borderRadius: Theme.borderRadius.xl,
    overflow: 'hidden',
  },
  emptyState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: Theme.spacing.xl,
    paddingVertical: Theme.spacing.xxxxl,
  },
  emptyIconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: 'rgba(20, 184, 166, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: Theme.spacing.lg,
  },
  emptyTitle: {
    fontSize: 20,
    fontFamily: Theme.typography.fontFamily.semiBold,
    color: Theme.colors.textPrimary,
    textAlign: 'center',
    marginBottom: Theme.spacing.sm,
  },
  emptyDescription: {
    fontSize: 14,
    fontFamily: Theme.typography.fontFamily.regular,
    color: Theme.colors.textSecondary,
    textAlign: 'center',
    lineHeight: 22,
    maxWidth: 300,
  },
  bottomPadding: {
    height: Theme.spacing.xxxl,
  },
  supportWidget: {
    marginHorizontal: Theme.spacing.xl,
    marginTop: Theme.spacing.xl,
    marginBottom: Theme.spacing.md,
    padding: Theme.spacing.lg,
    backgroundColor: 'rgba(20, 184, 166, 0.05)',
    borderRadius: Theme.borderRadius.lg,
    borderWidth: 1,
    borderColor: 'rgba(20, 184, 166, 0.15)',
  },
  supportContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: Theme.spacing.md,
    gap: Theme.spacing.sm,
  },
  supportTextContainer: {
    flex: 1,
  },
  supportText: {
    fontSize: 13,
    fontFamily: Theme.typography.fontFamily.regular,
    color: Theme.colors.textSecondary,
    lineHeight: 18,
  },
  supportButton: {
    paddingVertical: Theme.spacing.sm,
    paddingHorizontal: Theme.spacing.md,
    backgroundColor: Theme.colors.primary,
    borderRadius: Theme.borderRadius.md,
    alignSelf: 'flex-start',
  },
  supportButtonText: {
    fontSize: 13,
    fontFamily: Theme.typography.fontFamily.medium,
    color: '#FFFFFF',
  },
});

export default SavedScreen;

